/**
 * Enrollment reconciliation API contract (plan #005, U4).
 *
 * GET /v1/enrollment/reconciliation/orphans
 *
 * An admin-only, school-scoped read of a school's enrollment surface STATE plus
 * its orphan-checklist reconciliation facts. An orphan is a mirrored
 * student-checklist whose `candidateId` has no matching `UsersOnSchools.platform_id`
 * roster row at the school (KTD2). Orphans are a reconciliation FACT, never an
 * error (R7). Orphan rows are PII-free — the mirror stores only candidate ids and
 * checklist/step facts, never the BB student name/email (R15).
 *
 * The `state` distinguishes empty-vs-broken (R13/R14): a broken / token-dead sync
 * never reads as "all done" — it surfaces `BROKEN`, distinct from the legitimate
 * `EMPTY` (synced, zero rows). See `EnrollmentStateSchema`.
 */

import { z } from 'zod'

/**
 * The school's enrollment surface state, derived from the ENROLLMENTS
 * `PlatformDataSyncConfig` + the `ENROLLMENT` `SystemConfiguration` entry + mirror
 * row presence (R13/R14, KTD7):
 *
 * - `NOT_ENTITLED` — no enabled config / no ENROLLMENT entry and no mirror data.
 *   The school shows nothing.
 * - `SYNCING`      — enabled but never completed a successful sync
 *   (`lastSuccessfulSync` null). Not yet empty, not broken.
 * - `EMPTY`        — enabled + synced at least once + zero mirror rows. A FACT
 *   ("this school genuinely has no checklists"), distinct from `BROKEN`.
 * - `HEALTHY`      — synced + has mirror rows.
 * - `BROKEN`       — the last sync failed (`consecutiveFailures > 0` or an
 *   `errorMessage` is set). A broken/token-dead sync NEVER reads as "all done"
 *   (R14); it takes precedence over EMPTY/HEALTHY so stale data is never trusted.
 */
export const EnrollmentStateSchema = z.enum(['NOT_ENTITLED', 'SYNCING', 'EMPTY', 'HEALTHY', 'BROKEN'])
export type EnrollmentState = z.infer<typeof EnrollmentStateSchema>

/**
 * One orphan checklist row. PII-FREE — only the candidate id (== the BB
 * candidate id with no roster match) and the checklist's catalog facts. There is
 * no student name/email to leak (R15).
 */
export const EnrollmentOrphanChecklistSchema = z.object({
  /** BB candidate id of the mirrored checklist with no roster match. */
  candidateId: z.string(),
  /** Human checklist-type label (e.g. "Re-enrollment"), or null. */
  typeName: z.string().nullable(),
  /** Checklist school-year label (e.g. "2026 - 2027"; "" when BB carries none). */
  schoolYear: z.string(),
  /** When this checklist row was last mirrored (ISO 8601). */
  syncedAt: z.string()
})
export type EnrollmentOrphanChecklist = z.infer<typeof EnrollmentOrphanChecklistSchema>

export const GetEnrollmentReconciliationResponseSchema = z.object({
  /** The school's derived enrollment surface state (R13/R14). */
  state: EnrollmentStateSchema,
  /** Last successful ENROLLMENTS sync (ISO 8601), or null if never. */
  lastSuccessfulSync: z.string().nullable(),
  counts: z.object({
    /** Total mirrored checklist rows at the school. */
    checklists: z.number().int().nonnegative(),
    /** Of those, how many are orphans (no roster match). */
    orphans: z.number().int().nonnegative()
  }),
  /**
   * True when the orphan LIST was capped server-side. The server lists at most
   * 500 orphan rows per read (a defensive blast-radius limit, NOT a paging
   * boundary); `counts.orphans` remains the EXACT total. When `isTruncated` is
   * true, `orphans.length` (500) is less than `counts.orphans` — the consumer
   * should treat the list as a sample and trust the count.
   */
  isTruncated: z.boolean(),
  /** The orphan checklist rows (PII-free). Capped at 500 server-side. */
  orphans: z.array(EnrollmentOrphanChecklistSchema)
})
export type GetEnrollmentReconciliationResponse = z.infer<typeof GetEnrollmentReconciliationResponseSchema>
